<template>
  <app-page :title="$t('@newContractForm/title')">
    <v-layout row wrap>
      <v-flex xs12>
        <v-card flat>
          <errors-list :errorMessage="errorMessage" class="mt-0" />
          <v-form ref="form" v-model="formValid" @submit.prevent="onSubmit" color="green">
            <v-card-text>
              <v-container grid-list-lg class="ma-0 pa-0">
                <v-layout row wrap justify-start align-center class="pt-2">
                  <v-flex xs6>
                    <v-checkbox
                      v-model="form.selectedProductIds"
                      :label="productLabel(productsMapIds.TF)"
                      :value="productsMapIds.TF"
                      color="green darken-1"
                      class="mt-0"
                      :error-messages="ageValidationError(productsMapIds.TF)"
                      @change="onChangeProduct(), unselectTFProduct(productsMapIds.FamilyTF)"
                      hide-details
                    />
                    <v-checkbox
                      v-model="form.selectedProductIds"
                      :label="productLabel(productsMapIds.FamilyTF)"
                      :value="productsMapIds.FamilyTF"
                      color="green darken-1"
                      class="mt-0"
                      :error-messages="ageValidationError(productsMapIds.FamilyTF)"
                      @change="onChangeProduct(), unselectTFProduct(productsMapIds.TF)"
                      hide-details
                    />
                  </v-flex>
                  <v-flex xs6>
                    <v-select
                      v-if="canSelectVariation"
                      v-model="form.variationId"
                      :items="variationEntries"
                      :label="$t('@newContractForm/coveredAmountSelectLabel') + ' *'"
                      :hint="$t('@newContractForm/coveredAmountSelectHintText')"
                      :rules="canSelectVariation ? [validateRequired] : []"
                      single-line
                      class="my-0 pt-0"
                      persistent-hint
                      @change="fetchMonthlyCost(), validateAmount()"
                    ></v-select>
                  </v-flex>

                  <v-flex xs12>
                    <v-checkbox
                      v-model="form.selectedProductIds"
                      :label="productLabel(productsMapIds.OF)"
                      :value="productsMapIds.OF"
                      color="green darken-1"
                      class="mt-0"
                      :error-messages="ageValidationError(productsMapIds.OF)"
                      @change="fetchMonthlyCost()"
                      hide-details
                    />
                    <v-checkbox
                      v-model="form.selectedProductIds"
                      :label="productLabel(productsMapIds.SF)"
                      :value="productsMapIds.SF"
                      color="green darken-1"
                      class="mt-0"
                      :error-messages="ageValidationError(productsMapIds.SF)"
                      @change="fetchMonthlyCost()"
                      hide-details
                    />
                  </v-flex>
                </v-layout>
              </v-container>
            </v-card-text>
            <v-card-text>
              <v-container grid-list-lg class="ma-0 pa-0">
                <v-layout align-center>
                  <v-flex xs6>
                    <v-text-field
                      v-model="newApplicantSsn"
                      :disabled="!canAddApplicant"
                      :label="$t('@newContractForm/ssnInputLabel') + ' *'"
                      :hint="$t('@newContractForm/ssnInputHintText')"
                      :error-messages="ssnError"
                      persistent-hint
                      @input="ssnError = null"
                      @paste="onPasteSsn"
                      @keypress="(e) => onKeyPress(e, true)"
                    ></v-text-field>
                  </v-flex>

                  <v-flex xs6>
                    <v-btn
                      :disabled="submitting || !newApplicantSsn || !canAddApplicant"
                      :dark="!!newApplicantSsn"
                      :loading="ssnLoading"
                      color="green darken-1"
                      class="ma-0"
                      @click="onClickAddApplicant"
                    >
                      <v-icon class="mr-2">group_add</v-icon>
                      {{ $t('@newContractForm/addApplicantButtonLabel') }}
                    </v-btn>
                  </v-flex>
                </v-layout>

                <v-layout row justify-center align-center>
                  <v-flex xs6>
                    <v-radio-group
                      v-model="form.offerTransport"
                      :label="$t('@newContractForm/offerDeliveryRadioButtonsGroup/label')"
                      row
                      hide-details
                    >
                      <v-radio
                        :label="
                          $t('@newContractForm/offerDeliveryRadioButtonsGroup/smsOptionLabel')
                        "
                        value="sms"
                      ></v-radio>
                      <v-radio
                        :label="
                          $t('@newContractForm/offerDeliveryRadioButtonsGroup/emailOptionLabel')
                        "
                        value="email"
                      ></v-radio>
                    </v-radio-group>
                  </v-flex>

                  <v-flex xs6>
                    <v-radio-group
                      v-model="form.policyTransport"
                      :label="$t('@newContractForm/policyDeliveryRadioButtonsGroup/label')"
                      row
                      hide-details
                    >
                      <v-radio
                        :label="
                          $t('@newContractForm/policyDeliveryRadioButtonsGroup/letterOptionLabel')
                        "
                        value="letter"
                      ></v-radio>
                      <v-radio
                        :label="
                          $t('@newContractForm/policyDeliveryRadioButtonsGroup/emailOptionLabel')
                        "
                        value="email"
                      ></v-radio>
                    </v-radio-group>
                  </v-flex>
                </v-layout>
              </v-container>
            </v-card-text>

            <v-card flat v-for="(applicant, index) in form.applicants" :key="index">
              <v-divider />

              <v-card-title class="pb-0">
                <div class="subheading font-weight-bold pa-0 light-green--text text--darken-4">
                  {{ applicant.first_name }} {{ applicant.last_name }}
                </div>
                <v-spacer />
                <v-flex shrink>
                  <v-btn icon flat small color="pink" @click="onClickRemoveApplicant(index)">
                    <v-icon>delete</v-icon>
                  </v-btn>
                </v-flex>
              </v-card-title>

              <v-card-text>
                <v-container grid-list-lg class="ma-0 pa-0">
                  <v-layout row wrap justify-center align-center>
                    <v-flex xs4>
                      <v-text-field
                        v-model="applicant.social_security_number"
                        :label="$t('@newContractForm/applicantCard/ssnInputLabel') + ' *'"
                        color="green darken-1"
                        readonly
                      />
                    </v-flex>

                    <v-flex xs4>
                      <v-text-field
                        v-model="applicant.phone"
                        color="green darken-1"
                        :label="getApplicantPhoneInputLabel(applicant)"
                        :disabled="submitting"
                        name="phone"
                        mask="0## ### ## ##"
                        :placeholder="$t('@newContractForm/applicantCard/phoneInputPlaceholder')"
                        :rules="applicant.isContactPerson ? [validateRequired] : []"
                        :error-messages="validationError(applicant.errorField, ErrorFieldMap.phone)"
                      />
                    </v-flex>

                    <v-flex xs4>
                      <v-text-field
                        v-model="applicant.email"
                        color="green darken-1"
                        :label="getApplicantEmailInputLabel(applicant)"
                        :placeholder="$t('@newContractForm/applicantCard/emailInputPlaceholder')"
                        type="email"
                        :disabled="submitting"
                        :rules="applicant.isContactPerson ? [validateContactEmail] : []"
                        :error-messages="validationError(applicant.errorField, ErrorFieldMap.email)"
                      />
                    </v-flex>
                  </v-layout>

                  <v-layout
                    row
                    justify-center
                    align-center
                    v-if="
                      applicant.isContactPerson && settingsCollection.supports_bank_details.value
                    "
                  >
                    <v-flex xs4>
                      <v-select
                        :items="applicant.bankdata"
                        :disabled="submitting"
                        @change="onChangeBankDetails(index)"
                        v-model="applicant.bankDetailsId"
                        :label="$t('@newContractForm/applicantCard/bankDetailsSelectLabel')"
                        persistent-hint
                        item-value="id"
                        item-text="bank"
                        required
                      ></v-select>
                    </v-flex>

                    <v-flex xs2>
                      <v-text-field
                        v-model="applicant.bankName"
                        color="green darken-1"
                        :label="$t('@newContractForm/applicantCard/bankNameInputLabel')"
                        :placeholder="$t('@newContractForm/applicantCard/bankNameInputPlaceholder')"
                        :disabled="submitting || !!applicant.bankDetailsId"
                        :error-messages="
                          validationError(applicant.errorField, ErrorFieldMap.bankName)
                        "
                      />
                    </v-flex>

                    <v-flex xs2>
                      <v-text-field
                        v-model="applicant.clearingNumber"
                        color="green darken-1"
                        :label="$t('@newContractForm/applicantCard/clearingInputLabel')"
                        :placeholder="$t('@newContractForm/applicantCard/clearingInputPlaceholder')"
                        pattern="/[\d,-]{4,6}/"
                        :disabled="submitting || !!applicant.bankDetailsId"
                        type="tel"
                        maxlength="6"
                        @keypress="onKeyPress"
                        @input="onChangeClearing"
                        :error-messages="
                          validationError(applicant.errorField, ErrorFieldMap.clearingNumber)
                        "
                      />
                    </v-flex>

                    <v-flex xs4>
                      <v-text-field
                        v-model="applicant.bankNumber"
                        color="green darken-1"
                        :label="$t('@newContractForm/applicantCard/accountNumberInputLabel')"
                        :placeholder="
                          $t('@newContractForm/applicantCard/accountNumberInputPlaceholder')
                        "
                        :disabled="submitting || !!applicant.bankDetailsId"
                        maxlength="10"
                        @keypress="onKeyPress"
                        @input="validateBankAccount"
                        :error-messages="
                          validationError(applicant.errorField, ErrorFieldMap.bankNumber)
                        "
                      />
                    </v-flex>
                  </v-layout>
                  <v-flex
                    xs12
                    class="grey--text text--darken-1 warning-bank-account"
                    :class="bankAccountMayBeInvalid ? 'd-block' : 'd-none'"
                    v-if="!!applicant.isContactPerson"
                  >
                    <v-icon class="mr-1 d-inline" color="warning" size="1rem">warning</v-icon>
                    <span>{{ $t('@newContractForm/bankAccountMayBeInvalid') }}</span>
                  </v-flex>
                </v-container>

                <v-checkbox
                  :key="applicant.age"
                  :label="$t('@newContractForm/applicantCard/isContactPersonCheckboxLabel')"
                  :input-value="!!applicant.isContactPerson"
                  :readonly="applicant.isContactPerson || applicant.age <= 18"
                  @click="onClickContactPerson(applicant, index)"
                  :error-messages="errorMessagePerson(applicant)"
                  color="primary"
                  :hide-details="!contactPersonError"
                ></v-checkbox>
              </v-card-text>
            </v-card>

            <v-card flat v-if="form.applicants.length">
              <v-divider></v-divider>
              <v-card-text>
                <v-container grid-list-lg class="ma-0 pa-0">
                  <v-layout row wrap justify-start align-center>
                    <v-flex>
                      <v-checkbox
                        v-model="form.isEnsured"
                        :label="$t('@newContractForm/customerIsEnsured')"
                        color="green darken-1"
                        class="mt-0"
                        hide-details
                      />
                      <v-checkbox
                        v-model="form.onDirectDebit"
                        :label="$t('@newContractForm/customerIsOkWithDirectDebit')"
                        color="green darken-1"
                        class="mt-0"
                        required
                        hide-details
                        :rules="[validateRequired]"
                      />
                    </v-flex>
                  </v-layout>
                </v-container>
              </v-card-text>
            </v-card>
            <v-card v-if="showRecommendation" flat>
              <v-divider></v-divider>
              <v-card-title class="pb-0">
                <div class="subheading font-weight-bold pa-0 grey--text text--darken-3">
                  {{ $t('@newContractForm/recommendationForProduct') }}
                </div>
              </v-card-title>
              <v-card-text>
                <v-container grid-list-lg class="ma-0 pa-0">
                  <v-layout row wrap justify-start align-center>
                    <v-flex xs4>
                      <v-select
                        :items="recomendationEntries"
                        :disabled="submitting"
                        v-model="form.loanCost"
                        :label="$t('@newContractForm/loanCosts')"
                        :rules="canSelectVariation ? [validateRequired] : []"
                        :error-messages="
                          isInvalidAmount && form.loanCost
                            ? $t('@newContractForm/costMismatch')
                            : false
                        "
                        persistent-hint
                        required
                        hide-details
                      ></v-select>
                    </v-flex>
                    <v-flex xs4>
                      <v-select
                        :items="recomendationEntries"
                        :disabled="submitting"
                        v-model="form.housingCost"
                        :label="$t('@newContractForm/housingCosts')"
                        :error-messages="
                          isInvalidAmount && form.housingCost
                            ? $t('@newContractForm/costMismatch')
                            : false
                        "
                        persistent-hint
                        hide-details
                      ></v-select>
                    </v-flex>
                    <v-flex xs4>
                      <v-select
                        :items="recomendationEntries"
                        :disabled="submitting"
                        v-model="form.otherCost"
                        :label="$t('@newContractForm/otherCosts')"
                        :error-messages="
                          isInvalidAmount && form.otherCost
                            ? $t('@newContractForm/costMismatch')
                            : false
                        "
                        persistent-hint
                        hide-details
                      ></v-select>
                    </v-flex>
                  </v-layout>
                </v-container>
              </v-card-text>
              <v-card-title class="pb-0">
                <div
                  class="subheading my-2 mx-0 font-weight-bold"
                  :class="isInvalidAmount ? 'error--text' : ''"
                >
                  {{ $t('@newContractForm/recommendationSum', { recommendationSum }) }}
                </div>
              </v-card-title>
            </v-card>

            <template v-if="productCosts.length">
              <v-data-table :headers="summarizedHeaders" :items="productCosts" hide-actions>
                <template v-slot:items="props">
                  <td>{{ productLabel(props.item.productId) }}</td>
                  <td class="text-xs-right">
                    <div
                      class="column"
                      v-for="applicant in productApplicants(props.item.productId)"
                      :key="applicant.ssn"
                    >
                      <span>{{ applicant.social_security_number }}</span>
                    </div>
                  </td>
                  <td class="text-xs-right">{{ props.item.rest }} kr</td>
                  <td class="text-xs-right">{{ props.item.first }} kr</td>
                </template>
                <template v-slot:footer>
                  <td class="font-weight-bold">{{ $t('@newContractForm/total') }}</td>
                  <td></td>
                  <td class="font-weight-bold text-xs-right">{{ totalMonthlyCost }}</td>
                  <td class="font-weight-bold text-xs-right">{{ totalFirstPayment }}</td>
                </template>
              </v-data-table>
            </template>
          </v-form>
        </v-card>
      </v-flex>
    </v-layout>

    <template v-if="inIframe">
      <v-divider />
      <v-card-actions>
        <v-btn color="primary" class="ma-2 px-3" @click="onCalculation">
          Räkneexempel
        </v-btn>
        <v-spacer />
        <v-btn color="orange" class="mr-3 px-3 white--text" text="white" @click="onSave">
          Spara ansökan
        </v-btn>
        <v-btn color="success" class="ma-2 px-3" @click="onSubmit">Submit</v-btn>
      </v-card-actions>
    </template>

    <template v-else>
      <v-btn
        fab
        dark
        color="pink"
        top
        right
        absolute
        style="top: 32px; right: -80px"
        @click="onSubmit"
      >
        <v-icon>check</v-icon>
      </v-btn>
    </template>
  </app-page>
</template>

<script>
import Kontonummer from 'kontonummer';
import moment from 'moment';
import * as swedenNationalId from 'sweden-national-id';
import { mapState } from 'vuex';
import ErrorsList from '../../components/ErrorsList';
import { getErrorMessage, getErrorObject, validationError } from '../../errorHandlers';
import { catchException } from '../../plugins/bugsnag';
import {
  emailRegexp,
  onKeyPress,
  phonePrefixHardcode,
  phoneRegexp,
  serializeQuery,
} from '../../utils';

const msInYear = 365 * 24 * 3600 * 1000;

const productsMapIds = {
  TF: 11,
  FamilyTF: 12,
  OF: 10,
  SF: 9,
};

const ErrorFieldMap = {
  phone: 'phone',
  email: 'email',
  bankName: 'bankName',
  clearingNumber: 'clearingNumber',
  bankNumber: 'bankNumber',
};

const ErrorMessageMap = {
  phone: '@newContractForm/invalidPhoneErrorMessage',
  email: '@newContractForm/invalidEmailErrorMessage',
  clearingNumber: '@newContractForm/invalidClearingNumberErrorMessage',
  bankNumber: '@newContractForm/invalidAccountNumberErrorMessage',
  bankName: '@newContractForm/emptyBankNameErrorMessage',
};

export default {
  components: { ErrorsList },
  data() {
    return {
      errorMessage: null,
      submitting: null,
      formValid: true,
      isInvalidAmount: false,
      form: {
        selectedProductIds: [],
        variationId: 0,
        offerTransport: 'sms',
        policyTransport: 'email',
        applicants: [],
        isEnsured: false,
        onDirectDebit: false,
        loanCost: null,
        housingCost: null,
        otherCost: null,
      },
      summarizedHeaders: [
        {
          text: this.$t('@newContractForm/product'),
          align: 'left',
          value: 'name',
          sortable: false,
        },
        {
          text: this.$t('@newContractForm/applicants'),
          align: 'right',
          value: 'name',
          sortable: false,
        },
        {
          text: this.$t('@newContractForm/monthlyCost'),
          align: 'right',
          value: 'monthlyCost',
          sortable: false,
        },
        {
          text: this.$t('@newContractForm/firstPayment'),
          align: 'right',
          value: 'firstPayment',
          sortable: false,
        },
      ],
      productsMapIds,

      products: [],
      productCosts: [],

      newApplicantSsn: null,
      savedSsnIframe: null,
      ssnLoading: false,
      ssnError: '',
      ssnAgeError: '',
      onKeyPress,
      contactPersonError: '',
      validationError,
      ErrorFieldMap,
      bankAccountMayBeInvalid: false,
    };
  },

  computed: {
    ...mapState({
      currentUser: (state) => state.auth.currentUser,
    }),

    selectedProducts() {
      return this.products
        .filter((p) => this.form.selectedProductIds.includes(p.id))
        .map((el) => el);
    },

    variationEntries() {
      const variations = this.selectedProducts?.find((el) => el.variations)?.variations;
      if (!variations) {
        return [];
      }

      return variations.map((v) => ({
        value: v.id,
        text: v.covered_amount,
      }));
    },

    settingsCollection() {
      const productsSettings =
        (this.selectedProducts &&
          this.selectedProducts
            .filter((product) => !!product.settings)
            ?.flatMap((el) => el.settings)) ||
        [];

      let settings = [];
      const list = JSON.parse(JSON.stringify(productsSettings));
      list.map((item) => {
        const existing = settings
          .map((el) => el)
          .filter((v) => {
            return v.name === item.name;
          });
        let itemValue = item.value;
        if (Number.isInteger(Number(itemValue))) {
          itemValue = Number(item.value);
        } else if (itemValue.match(/^(true|false)$/i)) {
          itemValue = item.value === 'true';
        }

        if (existing.length) {
          const existingIndex = settings.indexOf(existing[0]);

          if (settings[existingIndex].name === 'max_age') {
            // Set minimum max age
            if (settings[existingIndex].value > itemValue) {
              settings[existingIndex].value = itemValue;
            }
          } else if (settings[existingIndex].value < itemValue) {
            settings[existingIndex].value = itemValue;
          }
        } else {
          settings.push({ ...item, value: itemValue });
        }
      });

      const dict = {
        supports_bank_details: { value: true, required: 0 },
        supports_variations: { value: false, required: 0 },
        min_people: { value: 1, required: 1 },
        max_people: { value: 1, required: 1 },
      };

      for (const item of settings) {
        dict[item.name] = { value: item.value, required: item.required };
      }

      return dict;
    },

    canSelectVariation() {
      return Boolean(!this.submitting && this.variationEntries.length && this.isProductTFSelected);
    },

    canAddApplicant() {
      return Boolean(
        !this.ssnLoading &&
          this.form.selectedProductIds?.length &&
          (this.form.variationId || !this.variationEntries.length),
      );
    },

    hasValidBankDetails() {
      return Boolean(
        this.contactPerson.bankDetailsId ||
          (this.contactPerson.clearingNumber &&
            this.contactPerson.bankNumber &&
            this.contactPerson.bankName),
      );
    },

    contactPerson() {
      return this.form.applicants.find((v) => v.isContactPerson);
    },

    inIframe() {
      try {
        return window.self !== window.top;
      } catch (e) {
        return true;
      }
    },
    showRecommendation() {
      return this.canSelectVariation && this.form.applicants.length;
    },
    recommendationSum() {
      return (
        Number(this.form.loanCost) + Number(this.form.housingCost) + Number(this.form.otherCost)
      );
    },
    recomendationEntries() {
      return [...Array(13).keys()].map((i) => i * 1000);
    },

    isProductTFSelected() {
      return this.form.selectedProductIds.some((p) =>
        [productsMapIds.TF, productsMapIds.FamilyTF].includes(p),
      );
    },

    totalMonthlyCost() {
      if (!this.productCosts.length) {
        return '0 kr';
      }

      const costs = this.productCosts.reduce((a, b) => a + b.rest, 0);
      return `${costs} kr`;
    },

    totalFirstPayment() {
      if (!this.productCosts.length) {
        return '0 kr';
      }

      const costs = this.productCosts.reduce((a, b) => a + b.first, 0);
      return `${costs} kr`;
    },

    isEmailRequired() {
      return this.form.policyTransport === 'email' || this.form.offerTransport === 'email';
    },
  },

  methods: {
    onPasteSsn(e) {
      const str = e.clipboardData.getData('Text');
      e.preventDefault();
      if (/^[0-9-+ ]+$/gi.test(str)) {
        this.newApplicantSsn = str.replace(/[^\d]/gi, '');
        return;
      }
      this.newApplicantSsn = null;
    },

    async onClickAddApplicant() {
      if (this.ssnLoading) {
        return;
      }

      if (this.newApplicantSsn.length === 13) {
        this.newApplicantSsn = this.newApplicantSsn.replace(/[^\d]/gi, '');
      }

      if (this.newApplicantSsn.length !== 12 && this.newApplicantSsn.length !== 10) {
        this.ssnError = this.$t('@newContractForm/invalidSsnErrorMessage');
        return;
      }

      let ssn;
      try {
        ssn = swedenNationalId.parse(this.newApplicantSsn);
      } catch (e) {
        this.ssnError = getErrorMessage(e);

        return;
      }

      if (!ssn || !ssn.dateOfBirth) {
        this.ssnError = this.$t('@newContractForm/invalidSsnErrorMessage');
        return;
      }
      if (this.form.applicants.find((v) => v.social_security_number === this.newApplicantSsn)) {
        this.ssnError = this.$t('@newContractForm/duplicatedApplicantErrorMessage');
        return;
      }

      if (this.form.applicants.length >= this.settingsCollection.max_people.value) {
        this.ssnError = this.$t('@newContractForm/tooManyApplicantsErrorMessage');
        return;
      }

      try {
        const ssnAgeInMilliseconds = moment().diff(ssn.dateOfBirth);

        if (this.form.applicants) {
          let adults = 0;
          let children = 0;
          this.form.applicants.forEach((el) => {
            if (el.isAdult) {
              ++adults;
            } else {
              ++children;
            }
          });

          if (
            this.settingsCollection.max_age &&
            this.settingsCollection.max_age.value <= ssnAgeInMilliseconds
          ) {
            this.ssnError = this.$t('@newContractForm/applicantAgeConstraintViolationErrorMessage');
            this.ssnAgeError = ssnAgeInMilliseconds;
            return;
          }

          this.ssnAgeError = null;
          const ssnAgeInYears = moment().diff(ssn.dateOfBirth, 'years');

          if (
            this.settingsCollection.max_adults &&
            this.settingsCollection.max_adults.value <= adults &&
            ssnAgeInYears >= 18
          ) {
            this.ssnError = this.$t('@newContractForm/tooManyAdultsErrorMessage');
            return;
          }
          if (
            this.settingsCollection.max_children &&
            this.settingsCollection.max_children.value <= children &&
            ssnAgeInYears < 18
          ) {
            this.ssnError = this.$t('@newContractForm/tooManyChildrenErrorMessage');
            return;
          }
        }
        this.ssnLoading = true;

        const query = serializeQuery({
          product_ids: this.form.selectedProductIds,
          ssn: this.newApplicantSsn,
        });

        await this.$http.get(`/api/products/availability?${query}`);

        const { data: personalData } = await this.$http.get(
          `/api/customer/profile/${this.newApplicantSsn}`,
        );
        const { bankdata, ...profile } = personalData;

        profile.phone = (profile.phone || '').replace(phonePrefixHardcode, '0');

        if (
          !this.form.applicants.find((applicant) => applicant.isContactPerson) &&
          profile.age >= 18
        ) {
          profile.isContactPerson = true;
        }
        profile.bankDetailsId = null;
        profile.bankdata = [
          {
            id: null,
            bank: this.$t('@newContractForm/applicantCard/bankDetailsSelectSetupNewOptionText'),
          },
          ...(bankdata || []),
        ];

        if (
          this.inIframe &&
          this.$store.state.auth.customer &&
          this.$store.state.auth.customer.ssn === this.newApplicantSsn
        ) {
          const { phone, email } = this.$store.state.auth.customer;
          profile.phone = (phone || '').replace(phonePrefixHardcode, '0') || profile.phone;
          profile.email = email || profile.email;
        }

        if (profile.phone && profile.phone.indexOf('+') !== -1) {
          profile.phone = profile.phone.slice(1);
        }

        this.form.applicants = [profile, ...this.form.applicants];
        this.newApplicantSsn = '';
        this.ssnError = '';
        await this.fetchMonthlyCost();
      } catch (e) {
        this.ssnError = getErrorMessage(e);
      } finally {
        this.ssnLoading = false;
      }
    },

    isMailValid(email) {
      return emailRegexp.test(email);
    },

    isPhoneValid(phone) {
      return phoneRegexp.test(phone);
    },

    getAgeFromSSN(ssn) {
      const ymd = this.formatSsn(ssn).substring(0, 6);
      const y = ymd.substring(0, 4);
      const m = ymd.substring(4, 6);
      const d = ymd.substring(6, 8);

      const birthDate = new Date([y, m, d].join('-'));
      const now = new Date();
      const ms = (now.getTime() - birthDate.getTime()) / msInYear;
      return Number(Math.floor(ms * 10000) / 10000); // keep 4 decimal digits for good age precision
    },

    formatSsn(ssn) {
      const cleanSsn = (ssn + '').replace(/[^\d]/gi, '');
      if (cleanSsn.length === 12) {
        return cleanSsn;
      } else {
        this.ssnError = this.$t('@newContractForm/invalidSsnFormatErrorMessage');
        return;
      }
    },

    onChangeClearing(e) {
      if (e.length === 5 && e.indexOf('-') === -1) {
        this.contactPerson.clearingNumber = e.substr(0, 4) + '-' + e.substr(-1);
      }
      this.validateBankAccount();
    },

    onChangeBankDetails(applicantIndex) {
      const applicant = this.form.applicants[applicantIndex];

      if (applicant && applicant.bankDetailsId) {
        const bd = applicant.bankdata.find((v) => v.id === applicant.bankDetailsId);
        applicant.bankName = bd.bank;
        applicant.bankNumber = bd.account;
        applicant.clearingNumber = bd.clearing;
      }
    },

    onClickRemoveApplicant(applicantIndex) {
      this.form.applicants = this.form.applicants.filter((v, i) => i !== applicantIndex);
      this.fetchMonthlyCost();
    },

    validateProductSettings() {
      const {
        min_people,
        max_people,
        supports_bank_details,
        supports_variations,
      } = this.settingsCollection;

      if (this.form.applicants.length < min_people.value) {
        return this.$t('@newContractForm/tooFewApplicantsDetailedErrorMessage', {
          minValue: min_people.value,
        });
      }
      if (this.form.applicants.length > max_people.value) {
        return this.$t('@newContractForm/tooManyApplicantsDetailedErrorMessage', {
          maxValue: max_people.value,
        });
      }

      if (
        supports_bank_details.required &&
        supports_bank_details.value &&
        !this.hasValidBankDetails
      ) {
        return this.$t('@newContractForm/bankDetailsRequired');
      }

      if (supports_variations.required && supports_variations.value && !this.form.variationId) {
        return this.$t('@newContractForm/emptyCoveredAmountErrorMessage');
      }

      if (!this.form.selectedProductIds?.length) {
        return this.$t('@newContractForm/emptyProductErrorMessage');
      }
    },

    validateData() {
      const contactPerson = this.form.applicants.find((applicant) => applicant.isContactPerson);

      const productError = this.validateProductSettings();

      if (productError) {
        return productError;
      }

      if (contactPerson) {
        const contactPersonSsn = contactPerson.social_security_number;
        if (this.contactPerson.email && !this.isMailValid(this.contactPerson.email)) {
          return { ssn: contactPersonSsn, field: ErrorFieldMap.email };
        }

        if (contactPerson.clearingNumber || contactPerson.bankNumber || contactPerson.bankName) {
          const errorBankDetails = this.validateBankDetails(contactPerson);

          if (errorBankDetails) {
            return errorBankDetails;
          }
        }
        this.validateBankAccount(contactPerson);

        if (!this.isPhoneValid(this.contactPerson.phone)) {
          return { ssn: contactPersonSsn, field: ErrorFieldMap.phone };
        }
      }

      const invalidEmail = this.form.applicants.find(
        (applicant) => applicant.email && !this.isMailValid(applicant.email),
      );

      const invalidPhone = this.form.applicants.find(
        (applicant) => applicant.phone && !this.isPhoneValid(applicant.phone),
      );

      if (invalidEmail) {
        return { ssn: invalidEmail.social_security_number, field: ErrorFieldMap.email };
      }

      if (invalidPhone) {
        return { ssn: invalidPhone.social_security_number, field: ErrorFieldMap.phone };
      }

      return false;
    },

    validateBankDetails(contactPerson) {
      const ssn = this.contactPerson.social_security_number;

      if (!/^[\d]{4}[-]{1}[\d]{1}$|^[\d]{4}$/.test(contactPerson.clearingNumber)) {
        return { ssn, field: ErrorFieldMap.clearingNumber };
      }

      if (!/^\d{6,10}$/.test(contactPerson.bankNumber)) {
        return { ssn, field: ErrorFieldMap.bankNumber };
      }

      return false;
    },

    validateRequired(val) {
      const isObject = val === Object(val);
      const isFilled = isObject ? Object.keys(val).length > 0 : !!val;
      return isFilled || this.$t('@newContractForm/emptyRequiredFieldErrorMessage');
    },

    validateContactEmail(val) {
      if (!val) {
        return !this.isEmailRequired || this.$t('@newContractForm/emptyRequiredFieldErrorMessage');
      }

      return this.isMailValid(val);
    },

    onSubmit() {
      this.clearApplicantErrors();

      if (this.submitting) {
        return;
      }
      if (!this.form.variationId && this.variationEntries.length) {
        return;
      }

      if (!this.contactPerson) {
        this.errorMessage = this.$t('@newContractForm/contactPersonNotSetErrorMessage');
        return;
      }

      if (this.isInvalidAmount) {
        this.errorMessage = this.$t('@newContractForm/coveredAmountNotSameErrorMessage');
        return;
      }

      this.newApplicantSsn = null;
      this.ssnError = null;
      this.$refs.form.validate();
      this.$nextTick(async () => {
        if (!this.formValid) {
          return;
        }

        const errorField = this.validateData();

        if (errorField) {
          if (typeof errorField === 'object') {
            this.highlightErrorField(errorField.ssn, errorField.field);
            this.errorMessage = this.$t(ErrorMessageMap[errorField.field]);
            return;
          }
          this.errorMessage = errorField;
          return;
        }

        try {
          this.submitting = true;

          let payload = {
            selectedProducts: this.form.selectedProductIds.map((id) => {
              const variationId = [productsMapIds.TF, productsMapIds.FamilyTF].includes(id)
                ? this.form.variationId
                : 0;
              return {
                applicants: this.productApplicants(id),
                productId: id,
                variationId,
              };
            }),
            offer_transport: this.form.offerTransport,
            policy_transport: this.form.policyTransport,
            phone: '+'.concat(this.contactPerson.phone),
            email: this.contactPerson.email,
            contact_person: this.contactPerson.social_security_number,
            reusable_bankdata_id: this.contactPerson.bankDetailsId || 0,
            advisor_id: this.currentUser.id,
            is_ensured: this.form.isEnsured,
            on_direct_debit: this.form.onDirectDebit,
            source: this.inIframe ? 'PRIVATE_LOAN_IFRAME' : 'INSURANCE_SYSTEM',
          };

          if (
            this.contactPerson.clearingNumber ||
            this.contactPerson.bankNumber ||
            this.contactPerson.bankName
          ) {
            payload = {
              ...payload,
              clearing: this.contactPerson.clearingNumber,
              account: this.contactPerson.bankNumber,
              bankname: this.contactPerson.bankName,
            };
          }

          if (this.isProductTFSelected && this.variationEntries.length) {
            payload = {
              ...payload,
              loan_cost: Number(this.form.loanCost),
              housing_cost: Number(this.form.housingCost),
              other_cost: Number(this.form.otherCost),
            };
          }

          const { data } = await this.$http.post('/api/insurances/bulk', payload);

          this.$router.push(`/contract/${data[0].internalId}`);
        } catch (e) {
          this.handleErrorMessage(e);
        } finally {
          this.submitting = false;
        }
      });
    },

    onSave() {
      const contactPerson = this.form.applicants.find((applicant) => applicant.isContactPerson);
      const ssn = this.savedSsnIframe || (contactPerson && contactPerson.social_security_number);
      if (ssn) {
        localStorage.setItem(ssn, JSON.stringify(this.form));
      }
    },

    onCalculation() {
      this.onSave();
      this.$router.push('/calculation');
    },

    onClickContactPerson(person, applicantIndex) {
      if (person.age < 18) {
        this.contactPersonError = person.social_security_number;
        return;
      }

      this.contactPersonError = '';
      this.form.applicants = this.form.applicants.map((applicant, i) => ({
        ...applicant,
        isContactPerson: applicantIndex === i,
      }));
    },

    async fetchProducts() {
      const { data } = await this.$http.get('/api/user/products');
      this.products = data;
    },

    onChangeProduct() {
      if (!this.isProductTFSelected) {
        this.form.variationId = 0;
      }
      this.fetchMonthlyCost();
    },

    unselectTFProduct(id) {
      this.form.selectedProductIds = this.form.selectedProductIds.filter((el) => el !== id);
    },

    async fetchMonthlyCost() {
      this.ssnAgeError = null;
      this.ssnError = null;

      const productError = this.validateProductSettings();

      if (productError) {
        this.productCosts = [];
        return;
      }

      try {
        const query = serializeQuery({
          product_ids: this.form.selectedProductIds,
          variation_id: this.form.variationId,
          ssns: this.form.applicants.map((v) => v.social_security_number),
          contact_person: this.contactPerson.social_security_number,
        });

        const { data } = await this.$http.get(`/api/product/costs?${query}`);
        this.productCosts = data;
      } catch (e) {
        this.productCosts = [];
        catchException(e);
      }
    },

    errorMessagePerson(person) {
      return this.contactPersonError === person.social_security_number
        ? this.$t('@newContractForm/contactPersonIsNotAdultErrorMessage')
        : '';
    },

    highlightErrorField(applicantSsn, field) {
      this.form.applicants = this.form.applicants.map((applicant) =>
        applicant.social_security_number === applicantSsn
          ? {
              errorField: field,
              ...applicant,
            }
          : { ...applicant },
      );
    },

    handleErrorMessage(e) {
      const error = getErrorObject(e);
      const ssn = error.property
        ? this.form.applicants[Number(error.property)].social_security_number
        : this.contactPerson.social_security_number;

      this.highlightErrorField(ssn, error.errorField);
      this.errorMessage = error.errorMessage;
    },

    clearApplicantErrors() {
      this.form.applicants = this.form.applicants.map((applicant) => {
        delete applicant['errorField'];
        delete applicant['errorText'];
        return { ...applicant };
      });
    },

    getApplicantPhoneInputLabel(applicant) {
      const label = this.$t('@newContractForm/applicantCard/phoneInputLabel');

      return applicant && applicant.isContactPerson ? label + ' *' : label;
    },

    getApplicantEmailInputLabel(applicant) {
      const label = this.$t('@newContractForm/applicantCard/emailInputLabel');

      return applicant && applicant.isContactPerson && this.isEmailRequired ? label + ' *' : label;
    },

    validateAmount() {
      if (this.showRecommendation && this.variationEntries.length) {
        const variation = this.selectedProducts
          .find((el) => el.variations)
          .variations.find((el) => el.id === this.form.variationId);
        if (variation && Number(variation.covered_amount) !== this.recommendationSum) {
          this.isInvalidAmount = true;
          return;
        }
        this.isInvalidAmount = false;
      }
    },

    validateBankAccount() {
      if (!this.contactPerson) {
        return;
      }

      this.bankAccountMayBeInvalid = false;
      if (!this.contactPerson.bankNumber || !this.contactPerson.clearingNumber) {
        return;
      }

      if (this.validateBankDetails(this.contactPerson)) {
        return;
      }

      try {
        const account = new Kontonummer(
          this.contactPerson.clearingNumber,
          this.contactPerson.bankNumber,
          {
            mode: 'lax',
          },
        );

        if (!account.valid) {
          this.bankAccountMayBeInvalid = true;
        }
      } catch (e) {
        this.bankAccountMayBeInvalid = true;
      }
    },

    productLabel(id) {
      const product = this.products?.find((el) => el.id === id);
      const productName = product?.name || '';

      let name = productName;
      if (product.id === productsMapIds.FamilyTF) {
        name = `${productName} ${this.$t('@newContractForm/familyLabel')}`;
      }

      return name;
    },

    productApplicants(productId) {
      const product = this.products.find((p) => p.id === productId);
      const maxPeople = product.settings.find((el) => el.name === 'max_people')?.value;
      const maxAdults = product.settings.find((el) => el.name === 'max_adults')?.value;
      const maxChildrens = product.settings.find((el) => el.name === 'max_adults')?.value;
      const applicants = !maxChildrens
        ? this.form.applicants.filter((a) => a.isAdult)
        : this.form.applicants.slice();

      if (maxAdults === maxPeople) {
        return applicants.slice(0, maxPeople);
      }

      if (Number(maxPeople) > 1) {
        return applicants.sort((a, b) => b.isAdult - a.isAdult).slice(0, Number(maxPeople));
      }

      return [this.contactPerson];
    },

    ageValidationError(productId) {
      if (!this.ssnAgeError) {
        return '';
      }

      const productSettings = this.selectedProducts?.find((p) => p.id === productId)?.settings;
      const maxAge = productSettings?.find((s) => s.name === 'max_age');
      if (maxAge && maxAge?.value <= this.ssnAgeError) {
        return this.$t('@newContractForm/applicantAgeConstraintViolationErrorMessage');
      }
      return '';
    },
  },
  watch: {
    recommendationSum(n, o) {
      if (n && n !== o) {
        this.validateAmount();
      }
    },
  },

  created() {
    this.fetchProducts();
    if (this.inIframe) {
      this.newApplicantSsn =
        (this.$store.state.auth.customer && this.$store.state.auth.customer.ssn) || null;
      this.savedSsnIframe = this.newApplicantSsn;
      const savedForm = localStorage.getItem(this.newApplicantSsn);
      if (savedForm) {
        const form = JSON.parse(savedForm);
        this.form = { ...form };
      }
    }
  },
};
</script>
<style scoped>
.warning-bank-account {
  margin-top: -12px;
  text-align: right;
  right: 14px;
  position: absolute;
}
</style>
